/**
 * AOZONE-103 — the per-category field rules for `create_entry`, GENERATED from
 * `CATEGORY_FIELD_SPEC` + `ENTRY_CATEGORIES` in slack-bot's `modalView.ts` (the
 * source of truth — CLAUDE.md, AOZONE-36). Nothing here transcribes a category
 * name or a field rule.
 *
 * Why this exists as its own module rather than living in `createEntry.ts`:
 *
 * 1. Two consumers need the same derivation. The MCP tool needs it as *prose*
 *    (Claude Code flattens away the input schema's `allOf` if/then conditionals,
 *    so per-category rules are invisible to the calling model unless they are in
 *    the description text) and as a block-id → MCP-parameter-name map for the
 *    error path. AOZONE-104 renders the same rules in the admin-ui.
 * 2. This module is a LEAF: it imports only `slack-bot/modalView` — no `ulid`,
 *    no `entryFromSubmission`, no AWS SDK — so a browser bundle can consume
 *    `CREATE_ENTRY_FIELD_RULES` without dragging the Lambda's dependency tree in.
 *
 * Per CLAUDE.md this is deliberately NOT moved into `@ozone/shared`: slack-bot
 * must not gain that dependency, and its Lambda bundle CodeSha256 is part of the
 * deploy-drift guard.
 */
import {
  ENTRY_CATEGORIES,
  CATEGORY_FIELD_SPEC,
  CUSTOMER_NAME_BLOCK_ID,
  PARTNER_OR_CLOUD_BLOCK_ID,
  TARGET_BLOCK_ID,
  TITLE_BLOCK_ID,
  DEAL_SIZE_AMOUNT_BLOCK_ID,
  DEAL_SIZE_TYPE_BLOCK_ID,
  ACTION_ITEM_BLOCK_ID,
  NARRATIVE_BODY_BLOCK_ID,
  type EntryCategory,
  type CategoryFieldSpec,
} from 'slack-bot/modalView';

/**
 * The MCP tool's entry-field parameter names, in canonical order. This order is
 * what the generated prose bullets and the generated error lines are rendered
 * in. `category` and `mbrPeriod` are excluded — they are not entry fields and
 * are not per-category.
 */
export const CREATE_ENTRY_FIELD_KEYS = [
  'customerName',
  'partnerOrCloud',
  'target',
  'title',
  'dealSizeAmount',
  'dealSizeType',
  'actionItem',
  'narrativeBody',
] as const;
export type CreateEntryFieldKey = (typeof CREATE_ENTRY_FIELD_KEYS)[number];

/**
 * The single bridge between `CategoryFieldSpec` keys and MCP parameter names.
 * Mostly 1:1; `dealSize` is one spec cell governing two MCP parameters.
 *
 * Typed as `Record<keyof CategoryFieldSpec, ...>` on purpose: adding a field to
 * `CategoryFieldSpec` in `modalView.ts` becomes a COMPILE error here, so the
 * generated prose and error mapping cannot silently ignore a new field.
 */
export const SPEC_FIELD_TO_PARAMS: Record<keyof CategoryFieldSpec, readonly CreateEntryFieldKey[]> =
  {
    customerName: ['customerName'],
    partnerOrCloud: ['partnerOrCloud'],
    target: ['target'],
    title: ['title'],
    dealSize: ['dealSizeAmount', 'dealSizeType'],
    actionItem: ['actionItem'],
    narrative: ['narrativeBody'],
  };

/**
 * A field's status for one category. `notApplicable` is the MCP-facing name for
 * the spec's `omit`: the Slack modal cannot show the field, and `create_entry`
 * rejects it rather than ignoring it.
 */
export type FieldRequirement = 'required' | 'optional' | 'notApplicable';

/**
 * Spec value → MCP-facing requirement. A TOTAL table rather than a ternary with
 * an else-branch: if `CategoryFieldSpec` ever gains a third status, a fallback
 * would silently advertise it as `required` in the prose, in the schema's `allOf`
 * and in the not-applicable gate. Keyed on the spec's own value union, so a new
 * status is a COMPILE error here instead.
 */
const SPEC_VALUE_TO_REQUIREMENT: Record<
  CategoryFieldSpec[keyof CategoryFieldSpec],
  FieldRequirement
> = {
  required: 'required',
  optional: 'optional',
  omit: 'notApplicable',
};

/** The spec values this module knows how to translate (for the drift test). */
export const KNOWN_SPEC_VALUES = Object.keys(SPEC_VALUE_TO_REQUIREMENT);

/** One category's complete field rule. Plain JSON-serializable data. */
export interface CategoryFieldRule {
  category: EntryCategory;
  /** Every MCP field key → its status. Canonical field order. */
  fields: Record<CreateEntryFieldKey, FieldRequirement>;
  /** Derived views of `fields`, each in canonical field order. */
  required: readonly CreateEntryFieldKey[];
  optional: readonly CreateEntryFieldKey[];
  notApplicable: readonly CreateEntryFieldKey[];
}

/** Derive the field rules for every category from the source of truth. */
export function buildCreateEntryFieldRules(): readonly CategoryFieldRule[] {
  return ENTRY_CATEGORIES.map((category) => {
    const spec = CATEGORY_FIELD_SPEC[category];
    const fields = {} as Record<CreateEntryFieldKey, FieldRequirement>;
    for (const specKey of Object.keys(SPEC_FIELD_TO_PARAMS) as Array<keyof CategoryFieldSpec>) {
      const requirement = SPEC_VALUE_TO_REQUIREMENT[spec[specKey]];
      for (const param of SPEC_FIELD_TO_PARAMS[specKey]) fields[param] = requirement;
    }
    // The three arrays are derived from `fields`, never written independently,
    // and inherit canonical field order from CREATE_ENTRY_FIELD_KEYS.
    const bucket = (want: FieldRequirement): CreateEntryFieldKey[] =>
      CREATE_ENTRY_FIELD_KEYS.filter((key) => fields[key] === want);
    return {
      category,
      fields,
      required: bucket('required'),
      optional: bucket('optional'),
      notApplicable: bucket('notApplicable'),
    };
  });
}

/**
 * The generated field rules. AOZONE-104 (admin-ui) consumes this to render the
 * same per-category rules the MCP tool advertises — import it rather than
 * re-deriving, and never transcribe it.
 */
export const CREATE_ENTRY_FIELD_RULES: readonly CategoryFieldRule[] = buildCreateEntryFieldRules();

/** `- <category>: required: a, b; optional: c; not applicable: d` */
function formatRuleLine(rule: CategoryFieldRule): string {
  const clauses: string[] = [];
  const push = (label: string, keys: readonly CreateEntryFieldKey[]): void => {
    if (keys.length > 0) clauses.push(`${label}: ${keys.join(', ')}`);
  };
  push('required', rule.required);
  push('optional', rule.optional);
  push('not applicable', rule.notApplicable);
  return `- ${rule.category}: ${clauses.join('; ')}`;
}

/**
 * The `create_entry` tool description, with the per-category rules restated in
 * prose. AOZONE-103 2a: the rules live in the input schema's `allOf`
 * conditionals too, but Claude Code flattens those away and presents the tool as
 * requiring only `category` + `narrativeBody`, so the calling model never sees
 * them. Prose survives flattening in any client.
 */
export function buildCreateEntryDescription(): string {
  return [
    'Create a single MBR entry attributed to the caller via their linked Slack identity. ' +
      'Equivalent to submitting the Slack /mbr create modal.',
    '',
    'Which fields apply depends on `category`. Some clients flatten the input schema\'s ' +
      'per-category conditionals, so the rules are restated here:',
    ...CREATE_ENTRY_FIELD_RULES.map(formatRuleLine),
    '',
    'Sending a not-applicable field is rejected, not ignored. `category` is always required. ' +
      '`mbrPeriod` is always optional (YYYY-MM, UTC) and defaults to the current month.',
  ].join('\n');
}

/**
 * AOZONE-103 2b — Slack modal block id → MCP parameter name.
 *
 * `create_entry` reuses slack-bot's `buildEntryFromSubmission` verbatim for
 * validation, and that keys its errors by the block id of the modal input it was
 * validating. An MCP caller has never seen those ids; they must be translated
 * back to the parameter names the caller actually passed. Keys are COMPUTED from
 * the imported constants so a renamed block id cannot leave a stale literal here.
 */
export const BLOCK_ID_TO_MCP_PARAM: Readonly<Record<string, CreateEntryFieldKey>> = {
  [CUSTOMER_NAME_BLOCK_ID]: 'customerName',
  [PARTNER_OR_CLOUD_BLOCK_ID]: 'partnerOrCloud',
  [TARGET_BLOCK_ID]: 'target',
  [TITLE_BLOCK_ID]: 'title',
  [DEAL_SIZE_AMOUNT_BLOCK_ID]: 'dealSizeAmount',
  [DEAL_SIZE_TYPE_BLOCK_ID]: 'dealSizeType',
  [ACTION_ITEM_BLOCK_ID]: 'actionItem',
  [NARRATIVE_BODY_BLOCK_ID]: 'narrativeBody',
};

/**
 * Re-key block-scoped validation errors onto MCP parameter names, preserving the
 * messages and their order. An unmapped key passes through verbatim rather than
 * being dropped — surfacing an odd key beats silently swallowing a real error.
 */
export function rekeyBuilderErrors(errors: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(errors).map(([key, message]) => [BLOCK_ID_TO_MCP_PARAM[key] ?? key, message]),
  );
}
